import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  Backdrop,
  LinearProgress,
  Stack,
  useTheme,
  Divider,
  Alert,
  Tab,
  Tabs,
  Slide,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  useMediaQuery,
} from "@mui/material";
import PaymentIcon from "@mui/icons-material/Payment";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { loadStripe } from "@stripe/stripe-js";
import { useDispatch } from "react-redux";
import {
  cancelCarrierSubscription,
  createCarrierSubscriptionSesison,
  getCarrierSubscriptions,
} from "../../redux/action/carrier";
import { unwrapResult } from "@reduxjs/toolkit";
import PricingOptions from "../pricingOptions/pricingOptions";

const PRICES = {
  bookingTool: 499,
  smsTool: 249,
  combo: 599,
};

const options = [
  {
    key: "bookingTool",
    label: "AutoBook + Support Bot",
  },
  {
    key: "smsTool",
    label: "Text-to-Inventory Assistant",
  },
  {
    key: "combo",
    label: "WeMovePro SmartSuite",
  },
];

const DAYS_INTERVAL = 30;
const REMINDER_DAYS_BEFORE = 3;

const Billing = ({ carrier }: any) => {
  const [isLoading, setIsLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState("");
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [isUpgrade, setIsUpgrade] = useState(false);
  const [tabIndex, setTabIndex] = useState(1);
  const [subscriptions, setSubscriptions] = useState<any[]>([]);
  const [filteredOptions, setFilteredOptions] = useState<any[]>(options);

  const strikeKey = process.env.REACT_APP_STRIPE_KEY || "";
  const theme = useTheme();
  const dispatch = useDispatch();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const payments = carrier.subscription_payments || [];
  useEffect(() => {
    setIsLoading(true);
    setLoadingMessage("Loading Details...");
    if (carrier) {
      dispatch<any>(getCarrierSubscriptions(carrier.id))
        .then(unwrapResult)
        .then((subs: any) => {
          setSubscriptions(subs.filter((s: any) => s.status !== "cancelled"));
          setTimeout(() => {
            setIsLoading(false);
            setLoadingMessage("");
          }, 500);
        })

        .catch(console.error);
    }
  }, [carrier, dispatch]);

  const formatDate = (dateStr: string | Date) =>
    new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    });

  const nextDueDate = (lastDate: Date | null) =>
    lastDate
      ? new Date(lastDate.getTime() + DAYS_INTERVAL * 24 * 60 * 60 * 1000)
      : null;

  const daysUntilNextPayment = (lastDate: Date | null) => {
    const due = nextDueDate(lastDate);
    if (!due) return null;
    const diffMs = due.getTime() - new Date().getTime();
    return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  };

  const findLastPayment = (payments: any[], predicate: (p: any) => boolean) => {
    const filtered = payments
      .filter(predicate)
      .sort(
        (a, b) =>
          new Date(b.checkout_time).getTime() -
          new Date(a.checkout_time).getTime()
      );
    return filtered.length > 0 ? filtered[0] : null;
  };

  const lastPayment = payments
    .slice()
    .sort(
      (a: any, b: any) =>
        new Date(b.checkout_time).getTime() -
        new Date(a.checkout_time).getTime()
    )[0];

  const lastPaymentIsCombo =
    lastPayment?.is_booking_tool_included && lastPayment?.is_sms_tool_included;

  const lastComboPayment = lastPaymentIsCombo ? lastPayment : null;
  const lastBookingPayment = findLastPayment(
    payments,
    (p) => p.is_booking_tool_included && !p.is_sms_tool_included
  );
  const lastSmsPayment = findLastPayment(
    payments,
    (p) => !p.is_booking_tool_included && p.is_sms_tool_included
  );

  const formatSubscriptionType = (type: string) => {
    const lower = type.toLowerCase();
    if (lower.includes("bookingtool")) return "AutoBook + Support Bot";
    if (lower.includes("smstool")) return "Text-to-Inventory Assistant";
    if (lower.includes("combo")) return "WeMovePro SmartSuite*";
    return type;
  };

  const isSubscribed = (type: string) => {
    return subscriptions.some(
      (sub) =>
        sub.status === "active" &&
        sub.subscription_type.toLowerCase().includes(type.toLowerCase())
    );
  };

  useEffect(() => {
    if (subscriptions.length === 0) {
      setFilteredOptions(options);
      return;
    }

    if (isSubscribed("combo")) {
      setFilteredOptions([]);
      return;
    }

    const hasBooking = isSubscribed("bookingtool");
    const hasSms = isSubscribed("smstool");

    if (hasBooking && hasSms) {
      setFilteredOptions([]);
      return;
    }

    if (hasBooking && !hasSms) {
      setFilteredOptions(
        options.filter((o) => o.key !== "bookingTool" && o.key !== "combo")
      );
      return;
    }

    if (!hasBooking && hasSms) {
      setFilteredOptions(
        options.filter((o) => o.key !== "smsTool" && o.key !== "combo")
      );
      return;
    }

    setFilteredOptions(options.filter((o) => o.key !== "combo"));
  }, [subscriptions]);

  const calculateChargeAmount = (key: string) => {
    if (key === "combo") return PRICES.combo;

    if (key === "bookingTool") {
      if (isSubscribed("smstool")) {
        return PRICES.combo - PRICES.smsTool;
      }
      return PRICES.bookingTool;
    }

    if (key === "smsTool") {
      if (isSubscribed("bookingtool")) {
        return PRICES.combo - PRICES.bookingTool;
      }
      return PRICES.smsTool;
    }

    return 0;
  };

  const handleSelectOption = (key: string) => {
    setSelectedOption((prev) => (prev === key ? null : key));

    if (key === "bookingTool" && isSubscribed("smstool")) {
      setIsUpgrade(true);
    } else if (key === "smsTool" && isSubscribed("bookingtool")) {
      setIsUpgrade(true);
    } else {
      setIsUpgrade(false);
    }
  };

  const handlePaymentDirect = async (optionKey: string) => {
    setIsLoading(true);
    setLoadingMessage("Redirecting to payment...");
    const stripe = await loadStripe(strikeKey);
    const amount = calculateChargeAmount(optionKey);
    const body = {
      carrierId: carrier.id,
      amountInCents: Math.round(amount * 100),
      type: optionKey,
      isUpgrade,
    };
    try {
      const responseSession = await dispatch<any>(
        createCarrierSubscriptionSesison(body)
      ).then(unwrapResult);

      await stripe?.redirectToCheckout({
        sessionId: responseSession.sessionId,
      });
    } catch (error) {
      console.error(error);
      setIsLoading(false);
    }
  };

  const PaymentCard = ({
    label,
    lastPayment,
    daysLeft,
  }: {
    label: string;
    lastPayment: any;
    daysLeft: number | null;
  }) => (
    <Box
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 2,
        border: `1px solid ${theme.palette.divider}`,
        mb: 2,
        boxShadow: 1,
        bgcolor: "background.paper",
      }}
    >
      <Typography
        variant={isMobile ? "body1" : "subtitle1"}
        fontWeight="bold"
        gutterBottom
        sx={{ wordBreak: "break-word" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        color="text.secondary"
        gutterBottom
        sx={{ wordBreak: "break-word" }}
      >
        Last Paid: {formatDate(lastPayment.checkout_time)} — $
        {lastPayment.amount_in_cents / 100}
      </Typography>
      {daysLeft !== null && (
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ wordBreak: "break-word" }}
        >
          Next Payment Due:{" "}
          {formatDate(nextDueDate(new Date(lastPayment.checkout_time))!)} (if
          not cancelled)
        </Typography>
      )}
      {daysLeft !== null &&
        daysLeft <= REMINDER_DAYS_BEFORE &&
        daysLeft > 0 && (
          <Alert
            severity="warning"
            icon={<WarningAmberIcon />}
            sx={{ fontWeight: 600, mt: 1 }}
          >
            You will be charged in {daysLeft} day{daysLeft > 1 ? "s" : ""}.
          </Alert>
        )}
    </Box>
  );
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState<any>(null);
  const openConfirmCancelDialog = (subscription: any) => {
    setSubscriptionToCancel(subscription);
    setConfirmCancelOpen(true);
  };

  const closeConfirmCancelDialog = () => {
    setSubscriptionToCancel(null);
    setConfirmCancelOpen(false);
  };

  const removeCarrierSubscription = (subscriptionId: string) => {
    setConfirmCancelOpen(false);
    setIsLoading(true);
    setLoadingMessage("Cancelling your subscription...");
    dispatch<any>(cancelCarrierSubscription(subscriptionId))
      .then(() => {
        setSubscriptions(
          subscriptions.filter(
            (sub: any) => sub.stripe_subscription_id !== subscriptionId
          )
        );
        setTimeout(() => {
          setIsLoading(false);
        }, 1500);
        if (selectedOption) {
          setSelectedOption(null);
        }
        closeConfirmCancelDialog();
      })
      .catch(console.error);
  };

  const SubscriptionListItem = ({
    subscription,
    openConfirmCancelDialog,
  }: {
    subscription: any;
    openConfirmCancelDialog: (sub: any) => void;
  }) => (
    <Box
      sx={{
        p: { xs: 1.5, sm: 2 },
        mb: 2,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "flex-start", sm: "center" },
        bgcolor: "background.paper",
        gap: { xs: 1, sm: 0 },
      }}
    >
      <Typography
        sx={{
          color: theme.palette.text.secondary,
          width: { xs: "100%", sm: 150 },
          wordBreak: "break-word",
        }}
      >
        {formatSubscriptionType(subscription.subscription_type)}
      </Typography>
      <Typography
        sx={{
          backgroundColor: "#008000",
          color: "#FFFFFF",
          py: 0.5,
          px: 1,
          borderRadius: 1,
          fontSize: 14,
          alignSelf: { xs: "flex-start", sm: "center" },
        }}
      >
        {subscription.status.toUpperCase()}
      </Typography>
      <Button
        variant="outlined"
        color="error"
        onClick={() => openConfirmCancelDialog(subscription)}
        size={isMobile ? "small" : "medium"}
        sx={{ alignSelf: { xs: "flex-start", sm: "center" } }}
      >
        Cancel
      </Button>
    </Box>
  );

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    if (newValue === 0) {
      closePanel();
    }
    setTabIndex(newValue);
  };

  // Sliding panel for payment and cancel actions
  const [panelOpen, setPanelOpen] = useState(false);

  useEffect(() => {
    setPanelOpen(selectedOption !== null);
  }, [selectedOption]);

  const closePanel = () => setSelectedOption(null);

  return (
    <Box
      sx={{
        mx: "auto",
        p: { xs: 2, sm: 3 },
        position: "relative",
        maxWidth: "100%",
      }}
    >
      <Typography
        sx={{
          fontWeight: 600,
          color: theme.palette.text.secondary,
          textAlign: "center",
          mb: 3,
          fontSize: { xs: "1rem", sm: "1.1rem" },
          wordBreak: "break-word",
        }}
      >
        YOUR SUBSCRIPTIONS & PAYMENTS
      </Typography>

      <Tabs
        value={tabIndex}
        onChange={handleTabChange}
        centered
        variant={isMobile ? "fullWidth" : "standard"}
        sx={{
          "& .MuiTab-root": {
            fontSize: { xs: "0.875rem", sm: "1rem" },
            minWidth: { xs: "auto", sm: 160 },
          },
        }}
      >
        <Tab label="Payments" />
        <Tab label="Subscriptions" />
      </Tabs>

      {tabIndex === 0 && (
        <Box sx={{ mt: 3 }}>
          {payments.length === 0 ? (
            <Typography
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                fontStyle: "italic",
              }}
            >
              No payments yet.
            </Typography>
          ) : lastPaymentIsCombo && lastComboPayment ? (
            <PaymentCard
              label="WeMovePro SmartSuite Subscription"
              lastPayment={lastComboPayment}
              daysLeft={daysUntilNextPayment(
                new Date(lastComboPayment.checkout_time)
              )}
            />
          ) : (
            <>
              {lastBookingPayment && (
                <PaymentCard
                  label="AutoBook + Support Bot Subscription"
                  lastPayment={lastBookingPayment}
                  daysLeft={daysUntilNextPayment(
                    new Date(lastBookingPayment.checkout_time)
                  )}
                />
              )}
              {lastSmsPayment && (
                <PaymentCard
                  label="Text-to-Inventory Assistant Subscription"
                  lastPayment={lastSmsPayment}
                  daysLeft={daysUntilNextPayment(
                    new Date(lastSmsPayment.checkout_time)
                  )}
                />
              )}
            </>
          )}
        </Box>
      )}

      {tabIndex === 1 && (
        <Box sx={{ mt: 3 }}>
          {subscriptions.length === 0 ? (
            <Typography
              sx={{
                textAlign: "center",
                color: theme.palette.text.secondary,
                mb: 3,
                fontStyle: "italic",
              }}
            >
              No active subscriptions found.
            </Typography>
          ) : (
            <>
              {!isMobile && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    px: 2,
                    py: 1,
                    borderBottom: `2px solid ${theme.palette.divider}`,
                    fontWeight: "bold",
                    color: theme.palette.text.primary,
                  }}
                >
                  <Box sx={{ flex: 2 }}>Subscription Type</Box>
                  <Box sx={{ flex: 1 }}>Status</Box>
                  <Box sx={{ flex: 1, textAlign: "right" }}>Action</Box>
                </Box>
              )}
              {!isLoading &&
                subscriptions.map((sub) => (
                  <SubscriptionListItem
                    key={sub.id}
                    subscription={sub}
                    openConfirmCancelDialog={openConfirmCancelDialog}
                  />
                ))}
            </>
          )}
          <Dialog
            open={confirmCancelOpen}
            onClose={closeConfirmCancelDialog}
            maxWidth="xs"
            fullWidth
            PaperProps={{
              sx: {
                m: { xs: 2, sm: 3 },
                width: { xs: "calc(100% - 32px)", sm: "auto" },
              },
            }}
          >
            <DialogTitle sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}>
              Confirm Cancellation
            </DialogTitle>
            <DialogContent>
              <Typography sx={{ wordBreak: "break-word" }}>
                Are you sure you want to cancel the subscription{" "}
                <strong>
                  {subscriptionToCancel &&
                    formatSubscriptionType(
                      subscriptionToCancel.subscription_type
                    )}
                </strong>
                ?
              </Typography>
            </DialogContent>
            <DialogActions
              sx={{
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Button
                onClick={closeConfirmCancelDialog}
                variant="outlined"
                fullWidth={isMobile}
              >
                No
              </Button>
              <Button
                onClick={() =>
                  subscriptionToCancel &&
                  removeCarrierSubscription(
                    subscriptionToCancel.stripe_subscription_id
                  )
                }
                color="error"
                variant="contained"
                fullWidth={isMobile}
              >
                Yes, Cancel
              </Button>
            </DialogActions>
          </Dialog>

          {filteredOptions.length > 0 && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography
                sx={{
                  textAlign: "center",
                  color: theme.palette.text.secondary,
                  mb: 2,
                }}
              >
                Subscribe to additional packages:
              </Typography>
              <Stack spacing={1} mb={3}>
                <PricingOptions
                  filteredOptions={filteredOptions}
                  calculateChargeAmount={calculateChargeAmount}
                  selectedOption={selectedOption}
                  handleSelectOption={handleSelectOption}
                />
              </Stack>
            </>
          )}
        </Box>
      )}

      {/* Sliding panel */}
      <Slide direction="left" in={panelOpen} mountOnEnter unmountOnExit>
        <Paper
          elevation={6}
          sx={{
            position: "fixed",
            top: 0,
            right: 0,
            height: "100vh",
            width: { xs: "100vw", sm: 320 },
            bgcolor: "background.paper",
            boxShadow: 4,
            p: { xs: 2, sm: 3 },
            zIndex: theme.zIndex.modal,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            overflow: "auto",
          }}
        >
          <Box>
            <Typography variant={isMobile ? "h6" : "h5"} gutterBottom>
              Confirm Subscription
            </Typography>
            <Typography sx={{ mb: 2, wordBreak: "break-word" }}>
              You selected:{" "}
              {options.find((o) => o.key === selectedOption)?.label || ""}
            </Typography>
            <Typography sx={{ fontWeight: "bold", mb: 3 }}>
              Price: ${calculateChargeAmount(selectedOption!)}
            </Typography>

            <Button
              variant="contained"
              color="primary"
              fullWidth
              startIcon={<PaymentIcon />}
              onClick={() => handlePaymentDirect(selectedOption!)}
              sx={{ mb: 2 }}
              size={isMobile ? "large" : "medium"}
            >
              Proceed to Pay
            </Button>
            <Button
              variant="outlined"
              color="error"
              fullWidth
              onClick={closePanel}
              size={isMobile ? "large" : "medium"}
            >
              Cancel
            </Button>
          </Box>
        </Paper>
      </Slide>

      {isLoading && (
        <Backdrop
          sx={{
            color:
              loadingMessage === "Cancelling your subscription..."
                ? "error"
                : "#fff",
            zIndex: (theme) => theme.zIndex.drawer + 1,
          }}
          open={isLoading}
          onClick={() => setIsLoading(false)}
        >
          <Box sx={{ width: { xs: "90%", sm: "50%" }, textAlign: "center" }}>
            <LinearProgress />
            <Typography
              color="#FFFFFF"
              fontSize={{ xs: 14, sm: 16 }}
              fontWeight={600}
              mt={1}
              sx={{ wordBreak: "break-word" }}
            >
              {loadingMessage}
            </Typography>
          </Box>
        </Backdrop>
      )}
    </Box>
  );
};

export default Billing;
